import type { Address } from "viem"
import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { resetNftInteraction } from "@/domains/interactions"
import { ResetNftActionsAdapter } from "@/domains/interactions/reset-nft/reset-nft-actions-adapter"
import { makeBreadcrumbs } from "@/domains/lock-page/breadcrumbs"
import { ResetCard } from "@/domains/lock-page/components/card/reset-card/components/reset-card"
import { LockPageLayout } from "@/domains/lock-page/components/lock-page-layout"
import { LockPageTabs } from "@/domains/lock-page/components/lock-page-tabs"
import { MANAGE_LOCK_TABS } from "@/domains/lock-page/constants"
import { ComplexTab } from "@/domains/navigation/components/complex-breadcrumb-dropdown"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useSyncLockIdFromRouter } from "@/hooks/use-sync-lock-id-from-router"
import useEventEmitter from "@/hooks/useEventEmitter"

export default observer(() => {
  const account = useAccount()
  const config = useConfig()
  const chainConfig = useChainConfig()
  const emitter = useEventEmitter()
  const scaleApp = useScaleApplication()
  const breadcrumbs = makeBreadcrumbs()

  useSyncLockIdFromRouter()

  const lock = scaleApp.Pages.Lock.selectedLock
  const nftId = lock?.id

  const onResetNftClick = async (nftId: number) => {
    const onChainActions = new ResetNftActionsAdapter()

    const result = await resetNftInteraction({
      account: account.address as Address,
      chainId: chainConfig.chainId,
      config,
      voterAddress: chainConfig.equalizer.contracts.voter,
      tokenId: nftId,
      emitter,
      eventBus: scaleApp.EventBus,
      onChainActions,
    })

    return result
  }

  const complexTab: ComplexTab | undefined = MANAGE_LOCK_TABS[3]

  if (!complexTab) {
    return null
  }

  const title = `Reset`

  return (
    <PageContainer>
      <MetaData title={title} />
      <PageTitle title={title} items={breadcrumbs} />
      <TooltipProvider>
        <LockPageLayout>
          <LockPageTabs />
          {lock && nftId && (
            <ResetCard
              lock={lock}
              icon={complexTab.icon ? <complexTab.icon /> : undefined}
              onReset={async () => {
                await onResetNftClick(lock.id)
              }}
            />
          )}
        </LockPageLayout>
      </TooltipProvider>
    </PageContainer>
  )
})
